export default function setStories() {
  const sections = document.querySelectorAll('[data-rd-stories]');
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTOPLAY_MS = 5000;

  const measureLength = cssLength => {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;width:${cssLength}`;
    document.body.appendChild(probe);
    const width = probe.offsetWidth;
    probe.remove();
    return width;
  };

  sections.forEach(section => {
    const panels = [...section.querySelectorAll('[data-story-panel]')];
    const texts = [...section.querySelectorAll('[data-story-text]')];
    const panelsRoot = section.querySelector('.rd-stories__panels');
    const isCta = section.classList.contains('rd-stories--cta');

    if (panels.length === 0 || !panelsRoot) return;

    // Cursos: o wrap é o item flex. Curadoria: o botão (wrap é display:contents).
    const flexItems = isCta
      ? panels.map(panel => panel.closest('.rd-stories__panel-wrap')).filter(Boolean)
      : panels;

    const transitionMs = (() => {
      const raw = getComputedStyle(section).getPropertyValue('--rd-stories-duration').trim();
      const value = Number.parseFloat(raw);
      if (!Number.isFinite(value)) return 820;
      return raw.endsWith('s') && !raw.endsWith('ms') ? value * 1000 : value;
    })();

    let collapsedPx = measureLength(
      getComputedStyle(section).getPropertyValue('--rd-stories-collapsed').trim() || '7.2rem'
    );

    let activeIndex = panels.findIndex(panel => panel.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;

    let hoverTimer = null;
    let unlockTimer = null;
    let autoplayTimer = null;
    let isSwitching = false;
    let pendingHoverIndex = null;
    let isHovering = false;
    let isInView = true;

    const clearHoverTimer = () => {
      if (hoverTimer == null) return;
      window.clearTimeout(hoverTimer);
      hoverTimer = null;
    };

    const clearUnlockTimer = () => {
      if (unlockTimer == null) return;
      window.clearTimeout(unlockTimer);
      unlockTimer = null;
    };

    const clearAutoplayTimer = () => {
      if (autoplayTimer == null) return;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const isDesktopInteractive = () => !mobileQuery.matches;

    const canAutoplay = () =>
      isDesktopInteractive() &&
      isInView &&
      !isHovering &&
      !reducedMotionQuery.matches &&
      !document.hidden &&
      panels.length > 1;

    const scheduleAutoplay = () => {
      clearAutoplayTimer();
      if (!canAutoplay()) return;

      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = null;
        if (!canAutoplay()) return;
        activate((activeIndex + 1) % panels.length);
      }, AUTOPLAY_MS);
    };

    const clearInlineWidths = () => {
      flexItems.forEach(item => {
        item.style.width = '';
        item.style.transition = '';
      });
      section.classList.remove('is-measured');
    };

    const setWidths = (index, { instant = false } = {}) => {
      if (!isDesktopInteractive()) {
        clearInlineWidths();
        return false;
      }

      const containerWidth = panelsRoot.clientWidth;
      if (containerWidth <= 0) return false;

      // Rem pode mudar depois de fontes/CSS — re-mede se ainda estiver em 0.
      if (collapsedPx <= 0) {
        collapsedPx = measureLength(
          getComputedStyle(section).getPropertyValue('--rd-stories-collapsed').trim() || '7.2rem'
        );
      }

      const styles = getComputedStyle(panelsRoot);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const totalGaps = gap * Math.max(flexItems.length - 1, 0);
      const available = containerWidth - totalGaps;
      const activeWidth = Math.max(available - collapsedPx * (flexItems.length - 1), collapsedPx);

      flexItems.forEach((item, i) => {
        const width = `${i === index ? activeWidth : collapsedPx}px`;

        if (instant) {
          item.style.transition = 'none';
          item.style.width = width;
          void item.offsetWidth;
          item.style.transition = '';
          return;
        }

        item.style.width = width;
      });

      section.classList.add('is-measured');
      return true;
    };

    const activate = (index, { fromHover = false, animate = true } = {}) => {
      if (index < 0 || index >= panels.length) return;
      if (index === activeIndex) return;

      if (isSwitching && fromHover) {
        pendingHoverIndex = index;
        return;
      }

      activeIndex = index;
      pendingHoverIndex = null;
      clearHoverTimer();
      clearAutoplayTimer();

      panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-expanded', String(isActive));
      });

      texts.forEach((text, i) => {
        const isActive = i === index;
        text.classList.toggle('is-active', isActive);
        text.hidden = !isActive;
      });

      setWidths(index, { instant: !animate });

      if (!animate || !isDesktopInteractive()) {
        scheduleAutoplay();
        return;
      }

      isSwitching = true;
      section.classList.add('is-switching');
      clearUnlockTimer();

      unlockTimer = window.setTimeout(() => {
        unlockTimer = null;
        isSwitching = false;
        section.classList.remove('is-switching');
        scheduleAutoplay();

        if (!isDesktopInteractive() || !hoverQuery.matches) {
          pendingHoverIndex = null;
          return;
        }

        const hovered = panels.findIndex(panel => panel.matches(':hover'));
        const next =
          pendingHoverIndex != null && panels[pendingHoverIndex]?.matches(':hover')
            ? pendingHoverIndex
            : hovered;

        pendingHoverIndex = null;
        if (next >= 0 && next !== activeIndex) activate(next, { fromHover: true });
      }, transitionMs);
    };

    const queueHover = index => {
      if (!isDesktopInteractive() || !hoverQuery.matches) return;
      if (index === activeIndex) return;

      if (isSwitching) {
        pendingHoverIndex = index;
        return;
      }

      clearHoverTimer();
      hoverTimer = window.setTimeout(() => {
        hoverTimer = null;
        if (!isDesktopInteractive() || index === activeIndex) return;
        activate(index, { fromHover: true });
      }, 140);
    };

    const syncMode = () => {
      clearHoverTimer();
      clearUnlockTimer();
      clearAutoplayTimer();
      isSwitching = false;
      pendingHoverIndex = null;
      section.classList.remove('is-switching');

      if (mobileQuery.matches) {
        panels.forEach(panel => {
          panel.classList.add('is-active');
          panel.setAttribute('aria-expanded', 'true');
        });
        clearInlineWidths();
        return;
      }

      const current = panels.findIndex(panel => panel.classList.contains('is-active'));
      activeIndex = current >= 0 ? current : 0;

      panels.forEach((panel, i) => {
        const isActive = i === activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-expanded', String(isActive));
      });

      texts.forEach((text, i) => {
        const isActive = i === activeIndex;
        text.classList.toggle('is-active', isActive);
        text.hidden = !isActive;
      });

      setWidths(activeIndex, { instant: true });
      scheduleAutoplay();
    };

    panels.forEach((panel, index) => {
      const hoverTarget = isCta ? flexItems[index] || panel : panel;

      panel.addEventListener('click', () => {
        if (!isDesktopInteractive()) return;
        clearHoverTimer();
        activate(index);
      });

      hoverTarget.addEventListener('mouseenter', () => {
        isHovering = true;
        clearAutoplayTimer();
        queueHover(index);
      });

      hoverTarget.addEventListener('mouseleave', () => {
        clearHoverTimer();
        if (pendingHoverIndex === index) pendingHoverIndex = null;

        // Só retoma se o ponteiro saiu de todos os painéis.
        const stillInside = panels.some(p => {
          const target = isCta ? p.closest('.rd-stories__panel-wrap') || p : p;
          return target.matches(':hover');
        });
        isHovering = stillInside;
        if (!isHovering) scheduleAutoplay();
      });

      panel.addEventListener('keydown', event => {
        if (!isDesktopInteractive()) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          clearHoverTimer();
          activate(index);
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          clearHoverTimer();
          activate((index + 1) % panels.length);
          panels[activeIndex].focus();
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          clearHoverTimer();
          activate((index - 1 + panels.length) % panels.length);
          panels[activeIndex].focus();
        }
      });
    });

    // Reaplica quando o container ganha largura real (hard refresh / fontes / CSS).
    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(() => {
        if (!isDesktopInteractive() || isSwitching) return;
        setWidths(activeIndex, { instant: true });
      });
      resizeObserver.observe(panelsRoot);
    }

    if (typeof IntersectionObserver === 'function') {
      const visibilityObserver = new IntersectionObserver(
        entries => {
          isInView = entries.some(entry => entry.isIntersecting);
          if (isInView) scheduleAutoplay();
          else clearAutoplayTimer();
        },
        { threshold: 0.35 }
      );
      visibilityObserver.observe(section);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearAutoplayTimer();
      else scheduleAutoplay();
    });

    window.addEventListener('load', () => {
      collapsedPx = measureLength(
        getComputedStyle(section).getPropertyValue('--rd-stories-collapsed').trim() || '7.2rem'
      );
      syncMode();
    });

    syncMode();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncMode);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(syncMode);
    }

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', scheduleAutoplay);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(scheduleAutoplay);
    }
  });
}
